import React, { useState } from 'react';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { SalesForm } from '../../components/SalesForm';
import { Ship, Package, Globe, FileText, Users, CheckCircle, ArrowRight, Bot } from 'lucide-react';
export function ShippingSolutionPage() {
  const [isSalesFormOpen, setIsSalesFormOpen] = useState(false);
  return <div className="flex flex-col min-h-screen w-full" data-id="element-1465">
      <Header data-id="element-1466" />
      <main className="flex-grow" data-id="element-1467">
        {/* Hero Section */}
        <div className="bg-black text-white py-16 md:py-24" data-id="element-1468">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1469">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-1470">
              <div data-id="element-1471">
                <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1472">
                  AI Solutions for Shipping Companies
                </h1>
                <p className="text-xl text-gray-300 mb-8" data-id="element-1473">
                  Streamline maritime operations, optimize vessel management,
                  and enhance supply chain visibility with AI-powered
                  automation.
                </p>
                <button onClick={() => setIsSalesFormOpen(true)} className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors" data-id="element-1474">
                  Request a Demo
                </button>
              </div>
              <div className="relative" data-id="element-1475">
                <img src="https://images.unsplash.com/photo-1494412651409-8963ce7935a7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Container ship" className="rounded-lg shadow-lg" data-id="element-1476" />
                <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-lg shadow-lg" data-id="element-1477">
                  <Ship className="text-black" size={32} data-id="element-1478" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Key Benefits */}
        <div className="py-16 md:py-24 bg-white" data-id="element-1479">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1480">
            <div className="text-center mb-16" data-id="element-1481">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1482">
                Key Benefits for Shipping Companies
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1483">
                Our AI agents address the complex challenges of global shipping
                operations and maritime logistics.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1484">
              <BenefitCard icon={<Globe size={24} data-id="element-1486" />} title="Global Visibility" description="Maintain real-time visibility across your entire fleet and cargo with automated tracking and reporting." data-id="element-1485" />
              <BenefitCard icon={<Package size={24} data-id="element-1488" />} title="Cargo Management" description="Optimize container utilization and streamline loading/unloading operations with AI-driven planning." data-id="element-1487" />
              <BenefitCard icon={<FileText size={24} data-id="element-1490" />} title="Documentation Automation" description="Process bills of lading, customs forms, and shipping manifests with high accuracy and minimal human intervention." data-id="element-1489" />
              <BenefitCard icon={<Users size={24} data-id="element-1492" />} title="Customer Communication" description="Keep customers informed with automated updates on shipment status, delays, and estimated arrival times." data-id="element-1491" />
              <BenefitCard icon={<CheckCircle size={24} data-id="element-1494" />} title="Regulatory Compliance" description="Stay compliant with international shipping regulations through automated documentation and verification." data-id="element-1493" />
              <BenefitCard icon={<Bot size={24} data-id="element-1496" />} title="Predictive Analytics" description="Forecast demand, optimize routes, and predict potential disruptions before they impact your operations." data-id="element-1495" />
            </div>
          </div>
        </div>
        {/* Features */}
        <div className="py-16 md:py-24 bg-white" data-id="element-1497">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1498">
            <div className="text-center mb-16" data-id="element-1499">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1500">
                Key Features for Shipping Companies
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1501">
                Our AI agents offer specialized capabilities designed for the
                unique needs of maritime shipping operations.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1502">
              <FeatureCard title="Vessel Performance Monitoring" description="AI agents track vessel performance metrics in real-time, identifying opportunities for fuel efficiency and maintenance optimization." data-id="element-1503" />
              <FeatureCard title="Port Documentation Automation" description="Automatically generate and process port entry documentation, reducing administrative burden and preventing delays." data-id="element-1504" />
              <FeatureCard title="Container Tracking & Management" description="Maintain visibility of all containers across your global network with real-time location updates and status changes." data-id="element-1505" />
              <FeatureCard title="Customs Clearance Assistance" description="Streamline customs processes with automated documentation verification and compliance checks for international shipments." data-id="element-1506" />
              <FeatureCard title="Weather Route Optimization" description="Analyze weather patterns and sea conditions to recommend optimal routes that balance safety, fuel efficiency, and delivery timelines." data-id="element-1507" />
              <FeatureCard title="Crew Management" description="Automate crew scheduling, certification tracking, and documentation to ensure compliance with maritime regulations." data-id="element-1508" />
            </div>
          </div>
        </div>
        {/* How It Works */}
        <div className="py-16 md:py-24 bg-gray-50" data-id="element-1509">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1510">
            <div className="text-center mb-16" data-id="element-1511">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1512">How It Works</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1513">
                Implementing Agentter.ai in your shipping operations is
                straightforward and delivers rapid results.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1514">
              <StepCard number="1" title="Connect Your Systems" description="Integrate with your existing shipping management systems, ERP, and documentation platforms." data-id="element-1515" />
              <StepCard number="2" title="Configure Your Agents" description="Define the tasks you want to automate using natural language - no coding required." data-id="element-1516" />
              <StepCard number="3" title="Deploy and Optimize" description="Launch your AI agents and continuously refine their performance based on operational feedback." data-id="element-1517" />
            </div>
          </div>
        </div>
        {/* CTA Section */}
        <div className="bg-black text-white py-16" data-id="element-1518">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1519">
            <h2 className="text-3xl font-bold mb-6" data-id="element-1520">
              Ready to transform your shipping operations?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto" data-id="element-1521">
              Schedule a personalized demo to see how Agentter.ai can automate
              and optimize your maritime logistics workflows.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-1522">
              <button onClick={() => setIsSalesFormOpen(true)} className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors" data-id="element-1523">
                Request a Demo
              </button>
              <a href="#" className="border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-900 transition-colors" data-id="element-1524">
                View Pricing
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-1525" />
      {/* Sales Form Modal */}
      <SalesForm isOpen={isSalesFormOpen} onClose={() => setIsSalesFormOpen(false)} agentType="Shipping Solutions" data-id="element-1526" />
    </div>;
}
function BenefitCard({
  icon,
  title,
  description
}) {
  return <div className="bg-gray-50 p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1527">
      <div className="bg-black text-white p-3 rounded-lg inline-block mb-4" data-id="element-1528">
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-2" data-id="element-1529">{title}</h3>
      <p className="text-gray-600" data-id="element-1530">{description}</p>
    </div>;
}
function StepCard({
  number,
  title,
  description
}) {
  return <div className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1531">
      <div className="bg-black text-white w-10 h-10 rounded-full flex items-center justify-center mb-4 font-bold" data-id="element-1532">
        {number}
      </div>
      <h3 className="text-xl font-semibold mb-2" data-id="element-1533">{title}</h3>
      <p className="text-gray-600" data-id="element-1534">{description}</p>
    </div>;
}
function ResultItem({
  text
}) {
  return <div className="flex items-start" data-id="element-1535">
      <CheckCircle className="text-green-500 mr-2 mt-1 flex-shrink-0" size={20} data-id="element-1536" />
      <span data-id="element-1537">{text}</span>
    </div>;
}
function FeatureCard({
  title,
  description
}) {
  return <div className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1538">
      <h3 className="text-xl font-semibold mb-3" data-id="element-1539">{title}</h3>
      <p className="text-gray-600" data-id="element-1540">{description}</p>
    </div>;
}